import { Fragment, Suspense, lazy, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDismissOnOutsideClick } from "../hooks/useDismissOnOutsideClick";
import { ChevronDown, ColumnsIcon, SortIcon } from "../icons";
import { PageBarKebabMenu } from "../pages/PageBarKebabMenu";
import { SearchActionDialogs } from "./actions/SearchActionDialogs";
import { SearchActionKey } from "./actions/SearchActionDialogs.types";
import { getSearchActionAvailability } from "./actions/searchActionAvailability";
import { SavedViewsDialogs } from "./savedViews/SavedViewsDialogs";
import { SavedViewDialog } from "./savedViews/SavedViewsDialogs.types";
import { SearchMenuProps } from "./SearchMenu.types";

const SearchColumnsModal = lazy(() =>
  import("./SearchColumnsModal").then((module) => ({ default: module.SearchColumnsModal }))
);
const SearchOrderModal = lazy(() =>
  import("./SearchOrderModal").then((module) => ({ default: module.SearchOrderModal }))
);

type Choice = SearchActionKey | SavedViewDialog | "columns" | "order";

interface Item {
  text: string;
  choice: Choice;
  group: number;
  /** Why the item cannot be used just now; when set, it is shown disabled with this beneath it. */
  reason?: string;
}

const ToolbarButtonClass =
  "border rounded-2 bg-white text-dark fw-medium hover-bg-light d-flex align-items-center gap-2 header-bar-view-select";

const SELECT_FIRST = "Select one or more records first";

/**
 * The one "..." menu of a search: the saved views, the columns and sort, then the actions
 * on the selected records. An item that cannot be used is shown disabled, with why.
 */
export function SearchMenu({
  search,
  allowedEntityTypes,
  savedViews,
  showActions = false,
  toolbar = false,
}: SearchMenuProps) {
  const [choice, setChoice] = useState<Choice | null>(null);
  const [viewsOpen, setViewsOpen] = useState(false);
  const viewsRef = useRef<HTMLDivElement>(null);

  useDismissOnOutsideClick(viewsRef, viewsOpen, () => setViewsOpen(false));

  const { query, submitted, selectedCount } = search;
  const available = getSearchActionAvailability(submitted?.entityName ?? query.entityName);
  const close = () => setChoice(null);

  // An action needs results to select from, then a selection, then an entity that offers it.
  const actionReason = (offered: boolean) =>
    !submitted
      ? "Run a search first"
      : !offered
      ? "Not available for this search"
      : selectedCount === 0
      ? SELECT_FIRST
      : undefined;

  const items: Item[] = [
    ...(savedViews
      ? ([
          { text: "Load Saved View", choice: "load", group: 0 },
          {
            text: "Save As Saved View",
            choice: "saveAs",
            group: 0,
            reason: savedViews.canManage ? undefined : "Administrators only",
          },
          {
            text: "Update Saved View",
            choice: "update",
            group: 0,
            reason: !savedViews.canManage
              ? "Administrators only"
              : savedViews.view
              ? undefined
              : "Load a saved view first",
          },
        ] as Item[])
      : []),
    { text: "Columns", choice: "columns", group: 1 },
    { text: "Sort", choice: "order", group: 1 },
    ...(showActions
      ? ([
          { text: "Export", choice: "export", group: 2, reason: actionReason(available.export) },
          { text: "Add to Static Marketing List", choice: "addToList", group: 2, reason: actionReason(available.marketingList) },
          { text: "Remove from Static Marketing List", choice: "removeFromList", group: 2, reason: actionReason(available.marketingList) },
          { text: "Bulk Edit", choice: "bulkEdit", group: 3, reason: actionReason(available.bulkEdit) },
          { text: "Activate", choice: "activate", group: 3, reason: actionReason(available.status) },
          { text: "Deactivate", choice: "deactivate", group: 3, reason: actionReason(available.status) },
        ] as Item[])
      : []),
  ];

  const savedViewChoices: Choice[] = ["load", "saveAs", "update"];
  const actionChoices: Choice[] = [
    "export",
    "addToList",
    "removeFromList",
    "bulkEdit",
    "activate",
    "deactivate",
  ];

  const menuItems = toolbar ? items.filter((item) => item.group >= 2) : items;

  const renderItem = (item: Item, closeMenu: () => void) => (
    <button
      type="button"
      className="header-bar-menu-item"
      disabled={Boolean(item.reason)}
      title={item.reason}
      onClick={() => {
        closeMenu();
        setChoice(item.choice);
      }}
    >
      {item.text}
    </button>
  );

  return (
    <>
      {toolbar && (
        <>
          <button
            type="button"
            className={ToolbarButtonClass}
            onClick={() => setChoice("columns")}
          >
            <FontAwesomeIcon icon={ColumnsIcon} />
            Columns
          </button>
          <button
            type="button"
            className={ToolbarButtonClass}
            onClick={() => setChoice("order")}
          >
            <FontAwesomeIcon icon={SortIcon} />
            Sort
          </button>
          {savedViews && (
            <div className="position-relative" ref={viewsRef}>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={viewsOpen}
                className={ToolbarButtonClass}
                onClick={() => setViewsOpen((previous) => !previous)}
              >
                Saved view
                <FontAwesomeIcon icon={ChevronDown} />
              </button>
              {viewsOpen && (
                <div className="header-bar-menu" role="menu">
                  {items
                    .filter((item) => item.group === 0)
                    .map((item) => (
                      <Fragment key={item.text}>
                        {renderItem(item, () => setViewsOpen(false))}
                      </Fragment>
                    ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {menuItems.length > 0 && (
        <PageBarKebabMenu>
          {(closeMenu) =>
            menuItems.map((item, index) => (
              <Fragment key={item.text}>
                {index > 0 && menuItems[index - 1].group !== item.group && (
                  <div className="header-bar-menu-divider" />
                )}
                {renderItem(item, closeMenu)}
              </Fragment>
            ))
          }
        </PageBarKebabMenu>
      )}
      {showActions && (
        <SearchActionDialogs
          search={search}
          active={choice && actionChoices.includes(choice) ? (choice as SearchActionKey) : null}
          onClose={close}
        />
      )}
      {savedViews && (
        <SavedViewsDialogs
          {...{ search, savedViews, allowedEntityTypes }}
          dialog={choice && savedViewChoices.includes(choice) ? (choice as SavedViewDialog) : null}
          onClose={close}
        />
      )}
      <Suspense fallback={null}>
        {choice === "columns" && (
          <SearchColumnsModal
            entityName={query.entityName}
            allowedEntityTypes={allowedEntityTypes}
            columns={query.columns}
            onApply={search.setColumns}
            onClose={close}
          />
        )}
        {choice === "order" && (
          <SearchOrderModal
            order={query.order}
            columns={query.columns}
            onApply={search.sortBy}
            onClose={close}
          />
        )}
      </Suspense>
    </>
  );
}
